import * as types from '../mutation-types';
import InternalChatAPI from '../../api/internalChat';
import { getFromCache, setCache } from 'shared/helpers/cache';

const INTERNAL_CHAT_MESSAGES_CACHE_TTL = 30 * 60 * 1000;

const internalChatMessagesCacheKey = (rootState, threadId) => {
  const accountId = Number(rootState?.route?.params?.accountId) || 'global';
  return `internal_chat_messages:${accountId}:${threadId}`;
};

const persistThreadMessagesCache = ($state, rootState, threadId) => {
  const messages = $state.messages[threadId];
  if (!messages || messages.length === 0) return;

  setCache(internalChatMessagesCacheKey(rootState, threadId), {
    messages,
    meta: $state.messagesMeta[threadId] || {},
  });
};

export const state = {
  users: [],
  threads: [],
  messages: {},
  activeThreadId: null,
  uiFlags: {
    isFetchingUsers: false,
    isFetchingThreads: false,
    isFetchingMessages: false,
    isCreatingThread: false,
    isSendingMessage: false,
  },
  messagesMeta: {},
};

export const getters = {
  getUsers: $state => $state.users,
  getThreads: $state => $state.threads,
  getActiveThreadId: $state => $state.activeThreadId,
  getActiveThread: $state =>
    $state.threads.find(t => t.id === $state.activeThreadId) || null,
  getMessages: $state => threadId =>
    $state.messages[threadId] || [],
  getMessagesMeta: $state => threadId =>
    $state.messagesMeta[threadId] || {},
  getUIFlags: $state => $state.uiFlags,
  getUnreadCount: $state =>
    $state.threads.reduce((sum, t) => sum + (t.unread_count || 0), 0),
};

export const actions = {
  hydrateMessagesFromCache: ({ commit, state: $state, rootState }, threadId) => {
    if (!threadId) return false;
    if (($state.messages[threadId] || []).length > 0) return true;

    const cached = getFromCache(
      internalChatMessagesCacheKey(rootState, threadId),
      INTERNAL_CHAT_MESSAGES_CACHE_TTL
    );

    if (!cached?.messages?.length) return false;

    commit(types.default.SET_INTERNAL_CHAT_MESSAGES, {
      threadId,
      messages: cached.messages,
      meta: cached.meta || {},
      prepend: false,
    });

    return true;
  },

  fetchUsers: async ({ commit, state: $state }, query = '') => {
    const isFirstLoad = $state.users.length === 0;
    if (isFirstLoad) {
      commit(types.default.SET_INTERNAL_CHAT_USERS_UI_FLAG, { isFetchingUsers: true });
    }
    try {
      const { data } = await InternalChatAPI.getUsers(query);
      commit(types.default.SET_INTERNAL_CHAT_USERS, data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[InternalChat] fetchUsers failed:', error?.response?.status, error?.message);
    } finally {
      if (isFirstLoad) {
        commit(types.default.SET_INTERNAL_CHAT_USERS_UI_FLAG, { isFetchingUsers: false });
      }
    }
  },

  fetchThreads: async ({ commit, state: $state }) => {
    const isPolling = $state.threads.length > 0;
    if (!isPolling) {
      commit(types.default.SET_INTERNAL_CHAT_THREADS_UI_FLAG, {
        isFetchingThreads: true,
      });
    }
    try {
      const { data } = await InternalChatAPI.getThreads();
      commit(types.default.SET_INTERNAL_CHAT_THREADS, data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[InternalChat] fetchThreads failed:', error?.response?.status, error?.message);
    } finally {
      if (!isPolling) {
        commit(types.default.SET_INTERNAL_CHAT_THREADS_UI_FLAG, {
          isFetchingThreads: false,
        });
      }
    }
  },

  createThread: async ({ commit }, recipientId) => {
    commit(types.default.SET_INTERNAL_CHAT_THREADS_UI_FLAG, {
      isCreatingThread: true,
    });
    try {
      const { data } = await InternalChatAPI.createThread(recipientId);
      commit(types.default.ADD_INTERNAL_CHAT_THREAD, data);
      commit(types.default.SET_ACTIVE_INTERNAL_CHAT_THREAD, data.id);
      return data;
    } finally {
      commit(types.default.SET_INTERNAL_CHAT_THREADS_UI_FLAG, {
        isCreatingThread: false,
      });
    }
  },

  createGroupThread: async ({ commit }, { groupName, memberIds, groupAvatar = null }) => {
    commit(types.default.SET_INTERNAL_CHAT_THREADS_UI_FLAG, {
      isCreatingThread: true,
    });

    try {
      const { data } = await InternalChatAPI.createGroupThread({
        groupName,
        memberIds,
        groupAvatar,
      });

      commit(types.default.ADD_INTERNAL_CHAT_THREAD, data);
      commit(types.default.SET_ACTIVE_INTERNAL_CHAT_THREAD, data.id);
      return data;
    } finally {
      commit(types.default.SET_INTERNAL_CHAT_THREADS_UI_FLAG, {
        isCreatingThread: false,
      });
    }
  },

  addMembersToThread: async ({ dispatch }, { threadId, memberIds }) => {
    await InternalChatAPI.addMembers(threadId, memberIds);
    await dispatch('fetchThreads');
  },

  leaveThread: async ({ commit, dispatch }, threadId) => {
    await InternalChatAPI.leaveThread(threadId);
    commit(types.default.SET_ACTIVE_INTERNAL_CHAT_THREAD, null);
    await dispatch('fetchThreads');
  },

  hideThread: async ({ commit }, threadId) => {
    await InternalChatAPI.hideThread(threadId);
    commit(types.default.UPDATE_INTERNAL_CHAT_THREAD, { threadId, hidden: true });
    commit(types.default.SET_ACTIVE_INTERNAL_CHAT_THREAD, null);
  },

  setActiveThread: ({ commit }, threadId) => {
    commit(types.default.SET_ACTIVE_INTERNAL_CHAT_THREAD, threadId);
  },

  fetchMessages: async ({ commit, state: $state, rootState }, { threadId, page = 1 }) => {
    commit(types.default.SET_INTERNAL_CHAT_MESSAGES_UI_FLAG, {
      isFetchingMessages: true,
    });
    try {
      const { data } = await InternalChatAPI.getMessages(threadId, page);
      commit(types.default.SET_INTERNAL_CHAT_MESSAGES, {
        threadId,
        messages: data.messages,
        meta: data.meta,
        prepend: page > 1,
      });
      persistThreadMessagesCache($state, rootState, threadId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[InternalChat] fetchMessages failed:', error?.response?.status, error?.message);
    } finally {
      commit(types.default.SET_INTERNAL_CHAT_MESSAGES_UI_FLAG, {
        isFetchingMessages: false,
      });
    }
  },

  sendMessage: async ({ commit, state: $state, rootState }, { threadId, content, attachments = [] }) => {
    commit(types.default.SET_INTERNAL_CHAT_MESSAGES_UI_FLAG, {
      isSendingMessage: true,
    });
    try {
      const { data } = await InternalChatAPI.sendMessage(threadId, content, attachments);
      commit(types.default.ADD_INTERNAL_CHAT_MESSAGE, {
        threadId,
        message: data,
      });
      commit(types.default.UPDATE_INTERNAL_CHAT_THREAD, {
        threadId,
        lastMessage: data,
      });
      commit(types.default.UPDATE_INTERNAL_CHAT_THREAD, { threadId, hidden: false });
      persistThreadMessagesCache($state, rootState, threadId);
      return data;
    } finally {
      commit(types.default.SET_INTERNAL_CHAT_MESSAGES_UI_FLAG, {
        isSendingMessage: false,
      });
    }
  },

  markRead: async ({ commit, state: $state, dispatch }, threadId) => {
    commit(types.default.UPDATE_INTERNAL_CHAT_THREAD, {
      threadId,
      unreadCount: 0,
    });

    const thread = ($state.threads || []).find(t => t.id === threadId);
    if (thread?.display_id) {
      dispatch('markMessagesRead', { id: thread.display_id }, { root: true });
    }

    try {
      await InternalChatAPI.markRead(threadId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[InternalChat] markRead failed:', error?.response?.status, error?.message);
    }
  },

  markReadByDisplayId: async ({ commit, state: $state }, displayId) => {
    const thread = ($state.threads || []).find(t => t.display_id === displayId);
    if (!thread) return;
    commit(types.default.UPDATE_INTERNAL_CHAT_THREAD, {
      threadId: thread.id,
      unreadCount: 0,
    });
    try {
      await InternalChatAPI.markRead(thread.id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[InternalChat] markRead failed:', error?.response?.status, error?.message);
    }
  },

  pollMessages: async ({ commit, dispatch, state: $state, rootState }, { threadId }) => {
    const current = $state.messages[threadId] || [];
    const lastId = current.length > 0 ? current[current.length - 1].id : null;
    if (!lastId) {
      return dispatch('fetchMessages', { threadId });
    }
    try {
      const { data } = await InternalChatAPI.getMessages(threadId, 1, lastId);
      if (data.messages && data.messages.length > 0) {
        commit(types.default.APPEND_INTERNAL_CHAT_MESSAGES, {
          threadId,
          messages: data.messages,
        });
        persistThreadMessagesCache($state, rootState, threadId);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[InternalChat] pollMessages failed:', error?.response?.status, error?.message);
    }
  },
};

export const mutations = {
  [types.default.SET_INTERNAL_CHAT_USERS]($state, users) {
    $state.users = users;
  },
  [types.default.SET_INTERNAL_CHAT_USERS_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.default.SET_INTERNAL_CHAT_THREADS]($state, threads) {
    const normalizedThreads = threads.map(thread => {
      if (thread.id === $state.activeThreadId) {
        return { ...thread, unread_count: 0 };
      }
      return thread;
    });

    const oldIds = $state.threads.map(t => `${t.id}:${t.unread_count}:${t.last_activity_at}:${t.last_message?.id}:${t.hidden}`);
    const newIds = normalizedThreads.map(t => `${t.id}:${t.unread_count}:${t.last_activity_at}:${t.last_message?.id}:${t.hidden}`);
    if (oldIds.join(',') !== newIds.join(',')) {
      $state.threads = normalizedThreads;
    }
  },
  [types.default.SET_INTERNAL_CHAT_THREADS_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.default.ADD_INTERNAL_CHAT_THREAD]($state, thread) {
    const exists = $state.threads.find(t => t.id === thread.id);
    if (!exists) {
      $state.threads.unshift(thread);
    }
  },
  [types.default.UPDATE_INTERNAL_CHAT_THREAD]($state, payload) {
    const index = $state.threads.findIndex(t => t.id === payload.threadId);
    if (index === -1) return;
    const thread = { ...$state.threads[index] };
    if (payload.lastMessage) {
      thread.last_message = {
        id: payload.lastMessage.id,
        content: payload.lastMessage.content,
        created_at: payload.lastMessage.created_at,
        sender_id: payload.lastMessage.sender?.id,
        sender_type: payload.lastMessage.sender?.type,
      };
      thread.last_activity_at = payload.lastMessage.created_at;
    }
    if (payload.unreadCount !== undefined) {
      thread.unread_count = payload.unreadCount;
    }
    if (payload.hidden !== undefined) {
      thread.hidden = payload.hidden;
    }
    $state.threads.splice(index, 1, thread);
    if (payload.lastMessage) {
      $state.threads.sort((a, b) => new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0));
    }
  },
  [types.default.SET_INTERNAL_CHAT_MESSAGES]($state, payload) {
    const { threadId, messages, meta, prepend } = payload;
    const current = $state.messages[threadId] || [];
    if (prepend) {
      $state.messages = {
        ...$state.messages,
        [threadId]: [...messages, ...current],
      };
    } else {
      $state.messages = {
        ...$state.messages,
        [threadId]: messages,
      };
    }
    $state.messagesMeta = {
      ...$state.messagesMeta,
      [threadId]: meta,
    };
  },
  [types.default.SET_INTERNAL_CHAT_MESSAGES_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.default.ADD_INTERNAL_CHAT_MESSAGE]($state, { threadId, message }) {
    const current = $state.messages[threadId] || [];
    const exists = current.some(m => m.id === message.id);
    if (exists) {
      $state.messages = {
        ...$state.messages,
        [threadId]: current.map(m => (m.id === message.id ? message : m)),
      };
    } else {
      $state.messages = {
        ...$state.messages,
        [threadId]: [...current, message],
      };
    }
  },
  [types.default.APPEND_INTERNAL_CHAT_MESSAGES]($state, { threadId, messages }) {
    const current = $state.messages[threadId] || [];
    const currentIndexById = new Map(current.map((message, index) => [message.id, index]));
    const merged = [...current];
    let changed = false;

    messages.forEach(message => {
      const existingIndex = currentIndexById.get(message.id);
      if (existingIndex === undefined) {
        merged.push(message);
        changed = true;
      } else if (JSON.stringify(merged[existingIndex]) !== JSON.stringify(message)) {
        merged[existingIndex] = message;
        changed = true;
      }
    });

    if (changed) {
      $state.messages = {
        ...$state.messages,
        [threadId]: merged,
      };
    }
  },
  [types.default.SET_ACTIVE_INTERNAL_CHAT_THREAD]($state, threadId) {
    $state.activeThreadId = threadId;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
